"use client";

import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Textarea } from "../../@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../@/components/ui/select";
import { toast } from "sonner";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import axios from "axios";
import { api_endpoint } from "../constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import { Form, FormField } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";
import { Field, FieldContent, FieldError, FieldLabel } from "../../@/components/ui/field";

// Validation schema
const updateCategorySchema = z.object({
    name: z.string().min(1, "This is required"),
    description: z.string().optional(),
    parentCategoryId: z
        .union([z.literal(""), z.coerce.number().int().positive("Parent ID must be a number")])
        .optional(),
});

export default function UpdateCategoryDialog({ 
    category, 
    categories = [], 
    onUpdated, 
    trigger = null 
}) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const { getToken } = useAuth();

    const form = useForm({
        resolver: zodResolver(updateCategorySchema),
        defaultValues: {
            name: category?.name || "",
            description: category?.description || "",
            parentCategoryId: category?.parent_category_id ? String(category.parent_category_id) : "",
        },
    });

    // Update form values if category prop changes
    useEffect(() => {
        form.reset({
            name: category?.name || "",
            description: category?.description || "",
            parentCategoryId: category?.parent_category_id ? String(category.parent_category_id) : "",
        });
    }, [category, form]);

    // Get parent categories for dropdown (exclude self and deeply nested ones)
    const getParentOptions = () => {
        if (!Array.isArray(categories)) return [];
        return categories.filter(cat => (cat?.level ?? 0) < 2 && cat.id !== category?.id);
    };

    const getPrefix = (level) => {
        if (level === 0) return "";
        if (level === 1) return "— ";
        if (level === 2) return "— — ";
        return "— ".repeat(level);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            const token = await getToken();
            if (!token) return;

            const payload = {
                id: category.id,
                name: data.name.trim(),
                description: data.description?.trim() || "",
                parent_category_id: data.parentCategoryId && data.parentCategoryId !== ""
                    ? Number(data.parentCategoryId)
                    : null,
            };

            console.log("Payload for update:", payload);
            const reqConfig = {
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                }
            };

            const response = await axios.put(api_endpoint + `/course/category/update/${category.id}`, payload, reqConfig);
            toast.success("Category updated successfully");
            setOpen(false);
            if (onUpdated) onUpdated();
        } catch (error) {
            console.error("Error updating category:", error);
            toast.error("Failed to update category. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.reset();
        setOpen(false);
    };

    const parentSelectValue = form.watch("parentCategoryId")
        ? String(form.watch("parentCategoryId"))
        : "none";

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger ? trigger : (
                    <Button variant="outline" size="sm">
                        Edit
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="h-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-(--neutral-900)">
                        Edit Category
                    </DialogTitle>
                    <DialogDescription className="font-body text-(--neutral-700)">
                        Edit the category details below.
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field, fieldState }) => (
                                <Field data-disabled={loading}>
                                    <FieldLabel>Name *</FieldLabel>
                                    <FieldContent>
                                        <Input
                                            {...field}
                                            id="name"
                                            placeholder="Enter category name"
                                            className={cn(
                                                "font-body",
                                                fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-destructive)"
                                            )}
                                            disabled={loading}
                                        />
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field, fieldState }) => (
                                <Field data-disabled={loading}>
                                    <FieldLabel>
                                        Description
                                        <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                            <Info className="w-3 h-3" />
                                            Optional
                                        </div>
                                    </FieldLabel>
                                    <FieldContent>
                                        <Textarea
                                            {...field}
                                            id="description"
                                            placeholder="Enter category description"
                                            rows={4}
                                            className={cn(
                                                "font-body resize-none",
                                                fieldState.error &&
                                                    "border-(--color-destructive) focus-visible:ring-(--color-destructive)"
                                            )}
                                            disabled={loading}
                                        />
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="parentCategoryId"
                            render={({ field, fieldState }) => (
                                <Field data-disabled={loading}>
                                    <FieldLabel>
                                        Parent Category
                                        <div className="flex items-center gap-1 text-xs text-(--neutral-600) font-body">
                                            <Info className="w-3 h-3" />
                                            Optional
                                        </div>
                                    </FieldLabel>
                                    <FieldContent>
                                        <Select
                                            value={parentSelectValue}
                                            onValueChange={(value) => field.onChange(value === "none" ? "" : value)}
                                            disabled={loading}
                                        >
                                            <SelectTrigger
                                                className={cn(
                                                    "font-body w-full",
                                                    fieldState.error &&
                                                        "border-(--color-destructive) focus:ring-(--color-destructive)"
                                                )}
                                            >
                                                <SelectValue placeholder="Select parent category" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">None (Root Category)</SelectItem>
                                                {getParentOptions().map((cat) => (
                                                    <SelectItem key={cat.id} value={cat.id.toString()}>
                                                        {getPrefix(cat.level)}{cat.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <FieldError
                                            errors={fieldState.error ? [{ message: fieldState.error.message }] : []}
                                        />
                                    </FieldContent>
                                </Field>
                            )}
                        />

                        {/* Action Buttons */}
                        <DialogFooter className="">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleCancel}
                                disabled={loading}
                                className="font-body"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant=""
                                disabled={loading}
                                className="font-body"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center w-full">
                                        <ClipLoader size={20} color="#fff" />
                                    </span>
                                ) : (
                                    "Update Category"
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}